import { forEach, map } from 'lodash-es'
import { FC, useCallback, useEffect, useState } from 'react'
import {
  Control,
  Controller,
  FieldArrayWithId,
  FieldErrors,
  UseFieldArrayRemove,
  UseFormRegister,
  UseFormSetValue,
  UseFormTrigger,
  UseFormWatch,
  useFieldArray,
} from 'react-hook-form'

import { TCurrency, TFestivalOrderTypes, TFormValues } from './GroupsTab'
import Loader from '../../../common/components/Loader'
import {
  IGroupFestivalPayload,
  PaymentProvider,
} from '../../../common/models/groups'
import FormGroup from '../../../core/components/FormGroup'
import {
  deleteGroupFestival,
  updateGroupFestival,
} from '../../../core/services/groups'
import { useAppSelector } from '../../../store'
import { DeleteConfirmationModal } from '../DeleteConfirmationModal'
import { MessageBox } from '../messages/MessageBox'
import { Button } from '@/components/ui/button'
import SelectField from '@/components/select-field'
import { Checkbox } from '@/components/ui/checkbox'
import { Card } from '@/components/ui'
import { Icon } from '@/common/components/Icon'

type OrderType = {
  value: TFestivalOrderTypes
  label: string
}

type Props = {
  festival: FieldArrayWithId<TFormValues, `groups.${number}.festivals`, 'id'>
  control: Control<TFormValues>
  errors: FieldErrors<TFormValues>
  register: UseFormRegister<TFormValues>
  index: number
  remove: UseFieldArrayRemove
  groupIndex: number
  watch: UseFormWatch<TFormValues>
  currenciesList?: TCurrency[]
  setValue: UseFormSetValue<TFormValues>
  trigger: UseFormTrigger<TFormValues>
  paymentProviders: PaymentProvider[]
}

export const GroupFestival: FC<Props> = ({
  errors,
  index,
  remove,
  groupIndex,
  watch,
  currenciesList,
  control,
  setValue,
  trigger,
  paymentProviders,
}): JSX.Element => {
  const [deleteConfirmation, setDeleteConfirmation] = useState<boolean>(false)
  const [
    deleteConfirmationPaymentProvider,
    setDeleteConfirmationPaymentProvider,
  ] = useState<boolean>(false)
  const [saving, setSaving] = useState<boolean>(false)
  const [deleteIndex, setDeleteIndex] = useState<number | undefined>()
  const [deletePaymentProviderIndex, setDeletePaymentProviderIndex] = useState<
    number | undefined
  >()
  const { festivals } = useAppSelector((s) => s.app)

  const orderTypes: OrderType[] = [
    { value: 'TICKETS', label: 'Tickets' },
    { value: 'CHECKIN', label: 'Check-in' },
    { value: 'TRANSFER', label: 'Transfer' },
    { value: 'TOPUP', label: 'Top-up' },
  ]

  const [isError, setIsError] = useState<boolean>(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')

  useEffect(() => {
    const festivals = watch(`groups.${groupIndex}.festivals`)
    forEach(festivals, (festival, i) => {
      const currencies = festival.currencies
      forEach(currencies, (currency, j) => {
        setValue(
          `groups.${groupIndex}.festivals.${i}.currencies.${j}.name`,
          currency.name,
        )
        setValue(
          `groups.${groupIndex}.festivals.${i}.currencies.${j}.id`,
          currency.id,
        )
      })
    })
  }, [currenciesList])

  const updateFestival = useCallback(async () => {
    const valid = await trigger(`groups.${groupIndex}.festivals.${index}`)
    if (!valid) return
    setSaving(true)
    try {
      const groupId = watch(`groups.${groupIndex}.id`)
      const festival = watch(`groups.${groupIndex}.festivals.${index}`)

      if (!groupId || !festival) return

      const festivalPayload: IGroupFestivalPayload = {
        festivalId: festival.id,
        currencies: map(festival.currencies, (currency) => ({
          id: currency?.id || '',
          active: currency.active as boolean,
          default: currency.main as boolean,
          name: currency.name,
        })),
        defaultPaymentProviderId: festival.defaultPaymentProviderId,
        festivalPaymentProviderDtos: map(
          festival.festivalPaymentProviderDtos,
          (provider) => ({
            orderTypes: provider.orderTypes,
            paymentProviderId: provider.paymentProviderId,
          }),
        ),
      }

      await updateGroupFestival(groupId, festival.id, festivalPayload)
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    } finally {
      setSaving(false)
    }
  }, [])

  const deleteFestival = useCallback(async () => {
    try {
      setSaving(true)
      const groupId = watch(`groups.${groupIndex}.id`)
      const festivalId = watch(`groups.${groupIndex}.festivals.${index}.id`)
      if (!groupId || !festivalId) return
      await deleteGroupFestival(groupId, festivalId)
      remove(index as number)
      setDeleteConfirmation(false)
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    } finally {
      setSaving(false)
    }
  }, [])

  const {
    fields: festivalPaymentProviderDtosFields,
    append: appendFestivalPaymentProviderDtos,
    remove: removeFestivalPaymentProviderDtos,
  } = useFieldArray({
    control: control,
    name: `groups.${groupIndex}.festivals.${index}.festivalPaymentProviderDtos`,
  })

  const festivalPaymentProviderDtos = watch(
    `groups.${groupIndex}.festivals.${index}.festivalPaymentProviderDtos`,
  )

  return (
    <>
      {saving && <Loader />}
      <Card className="py-4 px-4 mt-2 !bg-monochrome-3-60 space-y-2">
        <Controller
          control={control}
          name={`groups.${groupIndex}.festivals.${index}.id`}
          render={({ field }) => (
            <FormGroup label="Select Festival">
              <div className="flex w-full items-center justify-between gap-4">
                <SelectField
                  outline
                  error={
                    errors?.groups?.[groupIndex]?.festivals?.[index]?.id
                      ?.message
                  }
                  triggerClassName="h-[44px] rounded-2xl"
                  wrapperClassName="w-full"
                  placeholder="Select Festival"
                  options={map(festivals, (festival) => ({
                    value: festival.id,
                    label: festival.name,
                  }))}
                  value={field.value}
                  onValueChange={field.onChange}
                />
                <Button
                  variant="destructive"
                  className="w-[154px]"
                  type="button"
                  onClick={() => {
                    setDeleteConfirmation(true)
                    setDeleteIndex(index)
                  }}
                >
                  Delete
                  <Icon
                    icon="close-circle"
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    fill="none"
                  />
                </Button>
              </div>
            </FormGroup>
          )}
        />
        <Controller
          control={control}
          name={`groups.${groupIndex}.festivals.${index}.defaultPaymentProviderId`}
          render={({ field }) => (
            <Card className="bg-monochrome-3-60 border border-monochrome-120 p-4">
              <SelectField
                outline
                label="Default Payment Provider"
                triggerClassName="h-[44px] rounded-2xl"
                error={
                  errors?.groups?.[groupIndex]?.festivals?.[index]
                    ?.defaultPaymentProviderId?.message
                }
                placeholder="Select Default Payment Provider"
                options={map(paymentProviders, (provider) => ({
                  value: provider.id,
                  label: provider.title,
                }))}
                value={field.value}
                onValueChange={field.onChange}
              />
            </Card>
          )}
        />
        {map(festivalPaymentProviderDtosFields, (field, i) => {
          const festivalPaymentProviderDtos = watch(
            `groups.${groupIndex}.festivals.${index}.festivalPaymentProviderDtos`,
          )
          return (
            <Card
              key={field.id}
              className="bg-monochrome-3-60 border border-monochrome-120 p-4 mt-2"
            >
              <Controller
                name={`groups.${groupIndex}.festivals.${index}.festivalPaymentProviderDtos.${i}.paymentProviderId`}
                control={control}
                render={({ field: paymentProviderIdField }) => (
                  <FormGroup label="Select Payment Provider">
                    <SelectField
                      error={
                        errors?.groups?.[groupIndex]?.festivals?.[index]
                          ?.festivalPaymentProviderDtos?.[i]?.paymentProviderId
                          ?.message
                      }
                      outline
                      triggerClassName="h-[44px] rounded-2xl"
                      wrapperClassName="w-full"
                      placeholder="Select Payment Provider"
                      options={paymentProviders
                        .filter((one) => {
                          const festivalPaymentProviderDtos = watch(
                            `groups.${groupIndex}.festivals.${index}.festivalPaymentProviderDtos`,
                          )

                          const festivalPaymentProviderDtosIds =
                            festivalPaymentProviderDtos?.map(
                              (o) => o.paymentProviderId,
                            )

                          if (one.id === paymentProviderIdField.value) {
                            return true
                          }

                          return !festivalPaymentProviderDtosIds?.includes(
                            one.id,
                          )
                        })
                        .map((provider) => ({
                          value: provider.id,
                          label: provider.title,
                        }))}
                      value={paymentProviderIdField.value}
                      onValueChange={paymentProviderIdField.onChange}
                    />
                  </FormGroup>
                )}
              />
              <div className="grid grid-cols-4 divide-x divide-monochrome-120 gap-4 border-b border-monochrome-120 pb-4 mb-4 mt-4">
                {map(orderTypes, (orderType, ind) => {
                  const currentValue = watch(
                    `groups.${groupIndex}.festivals.${index}.festivalPaymentProviderDtos.${i}.orderTypes`,
                  )
                  return (
                    <Controller
                      key={`active-${ind}`}
                      name={`groups.${groupIndex}.festivals.${index}.festivalPaymentProviderDtos.${i}.orderTypes`}
                      control={control}
                      render={({ field: orderTypeField }) => (
                        <FormGroup
                          label={orderType.label}
                          labelClassName="whitespace-nowrap"
                          inputWrapperClassName="w-auto"
                          wrapperClassName="flex-row-reverse justify-end w-auto pe-4"
                          direction="row"
                          labelProps={{
                            htmlFor: `order-${ind}`,
                          }}
                        >
                          <Checkbox
                            id={`order-${ind}`}
                            disabled={
                              !orderTypeField.value?.includes(
                                orderType.value as TFestivalOrderTypes,
                              ) &&
                              festivalPaymentProviderDtos?.some((o) =>
                                o.orderTypes?.includes(orderType.value),
                              )
                            }
                            onCheckedChange={() => {
                              if (!currentValue) {
                                return
                              }

                              const newValue = currentValue.includes(
                                orderType.value,
                              )
                                ? currentValue.filter(
                                    (one) => one !== orderType.value,
                                  )
                                : [...currentValue, orderType.value]

                              setValue(
                                `groups.${groupIndex}.festivals.${index}.festivalPaymentProviderDtos.${i}.orderTypes`,
                                newValue,
                              )
                            }}
                            checked={currentValue?.includes(
                              orderType.value as TFestivalOrderTypes,
                            )}
                          />
                        </FormGroup>
                      )}
                    />
                  )
                })}
              </div>
              <div className="flex justify-end [&>*]:w-[154px]">
                <Button
                  variant="destructive"
                  type="button"
                  className="w-[154px]"
                  onClick={() => {
                    setDeleteConfirmationPaymentProvider(true)
                    setDeletePaymentProviderIndex(i)
                  }}
                >
                  Delete
                  <Icon
                    icon="close-circle"
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    fill="none"
                  />
                </Button>
              </div>
            </Card>
          )
        })}
        {festivalPaymentProviderDtos &&
          festivalPaymentProviderDtos?.length < paymentProviders.length && (
            <Button
              onClick={() => {
                appendFestivalPaymentProviderDtos({
                  paymentProviderId: '',
                  orderTypes: [],
                })
              }}
              variant="secondary"
              className="w-full mt-2 mb-2"
            >
              Add New Payment Provider
            </Button>
          )}
        {currenciesList && currenciesList?.length > 0 && (
          <div className="card-gray-with-border mt-2">
            <h4 className="mb-2 text-title-xs text-monochrome-1">
              Select Active Currencies
            </h4>
            <div className="grid grid-cols-4 divide-x divide-monochrome-120 gap-4 border-b border-monochrome-120 pb-4 mb-4 mt-4">
              {map(currenciesList, (currency, ind) => (
                <Controller
                  key={`active-${ind}`}
                  name={`groups.${groupIndex}.festivals.${index}.currencies.${ind}.active`}
                  control={control}
                  render={({ field }) => (
                    <FormGroup
                      label={currency.name}
                      labelClassName="whitespace-nowrap"
                      inputWrapperClassName="w-auto"
                      wrapperClassName="flex-row-reverse justify-end w-auto pe-4"
                      direction="row"
                      labelProps={{
                        htmlFor: `active-${ind}`,
                      }}
                    >
                      <Checkbox
                        id={`active-${ind}`}
                        onCheckedChange={field.onChange}
                        checked={field.value || false}
                      />
                    </FormGroup>
                  )}
                />
              ))}
            </div>
            <h4 className="mb-2 text-title-xs text-monochrome-1">
              Select Main Currency
            </h4>
            <div className="grid grid-cols-4 divide-x divide-monochrome-120 gap-4 border-b border-monochrome-120 pb-4 mb-4 mt-4">
              {map(currenciesList, (currency, ind) => (
                <Controller
                  key={`main-${ind}`}
                  name={`groups.${groupIndex}.festivals.${index}.currencies.${ind}.main`}
                  control={control}
                  render={({ field }) => (
                    <FormGroup
                      label={currency.name}
                      labelClassName="whitespace-nowrap"
                      inputWrapperClassName="w-auto"
                      wrapperClassName="flex-row-reverse justify-end w-auto pe-4"
                      direction="row"
                      labelProps={{
                        htmlFor: `main-${ind}`,
                      }}
                    >
                      <Checkbox
                        id={`main-${ind}`}
                        onCheckedChange={(checked) => {
                          const festivalCurrencies = watch(
                            `groups.${groupIndex}.festivals.${index}.currencies`,
                          )
                          festivalCurrencies?.forEach((_, i) => {
                            setValue(
                              `groups.${groupIndex}.festivals.${index}.currencies.${i}.main`,
                              i === ind ? checked === true : false,
                            )
                          })
                        }}
                        checked={field.value || false}
                      />
                    </FormGroup>
                  )}
                />
              ))}
            </div>
          </div>
        )}
        <div className="flex gap-2 justify-end mt-2 [&>*]:w-[154px]">
          <Button
            variant="secondary"
            className="flex items-center justify-center"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            type="button"
            className="flex items-center justify-center"
            onClick={updateFestival}
          >
            Save
          </Button>
        </div>
      </Card>
      <DeleteConfirmationModal
        showDeleteConfirmationModal={deleteConfirmation}
        setDeleteConfirmationModal={setDeleteConfirmation}
        deleteTitle={'Festival'}
        deleteFunc={() => {
          const saved = watch(`groups.${groupIndex}.festivals.${index}.saved`)
          if (saved) {
            deleteFestival()
          } else {
            remove(deleteIndex as number)
            setDeleteConfirmation(false)
          }
        }}
      />
      <DeleteConfirmationModal
        showDeleteConfirmationModal={deleteConfirmationPaymentProvider}
        setDeleteConfirmationModal={setDeleteConfirmationPaymentProvider}
        deleteTitle={'Payment Provider'}
        deleteFunc={() => {
          removeFestivalPaymentProviderDtos(deletePaymentProviderIndex)
          setDeleteConfirmation(false)
        }}
      />
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </>
  )
}
